import { ref, computed, watch } from 'vue'
import type { MarketFiltersData, MarketSelectItem, MarketSortOption } from '@/types/market'

export type MarketFilterItem = MarketSelectItem

interface MarketFiltersProps {
  languagesList?: MarketFilterItem[]
  coursesList?: MarketFilterItem[]
  productTypesList?: MarketFilterItem[]
  holidaysList?: MarketFilterItem[]
}

export type FiltersData = MarketFiltersData
export type SortOption = MarketSortOption

export function useMarketFilters(
  _props: MarketFiltersProps,
  emit: (event: 'filters-changed', data: FiltersData) => void
) {
  const selectedLanguage = ref('')
  const searchText = ref('')
  const isAuthored = ref(false)
  const isOwned = ref(false)
  const isAvailable = ref(false)
  const selectedCourses = ref<number[]>([])
  const selectedTypes = ref<number[]>([])
  const selectedHolidays = ref<number[]>([])
  const priceFrom = ref('')
  const priceTo = ref('')
  const currentSort = ref('-creation_date')

  const sorts: Record<string, SortOption> = {
    created: {
      text: 'По новизне',
      desc: '-creation_date',
      asc: 'creation_date'
    },
    price: {
      text: 'По цене',
      desc: '-price',
      asc: 'price'
    },
    rating: {
      text: 'По рейтингу',
      desc: '-avg_rating',
      asc: 'avg_rating'
    }
  }

  const query = new URLSearchParams(window.location.search)
  isOwned.value = query.has('owned')
  if (query.has('content_types')) {
    selectedTypes.value = query
      .get('content_types')!
      .split(',')
      .map(e => Number(e))
  }
  if (query.has('courses')) {
    selectedCourses.value = query
      .get('courses')!
      .split(',')
      .map(e => Number(e))
  }
  if (query.has('holidays')) {
    selectedHolidays.value = query
      .get('holidays')!
      .split(',')
      .map(e => Number(e))
  }

  const filtersData = computed<FiltersData>(() => ({
    min_price: priceFrom.value,
    max_price: priceTo.value,
    search_text: searchText.value,
    language: selectedLanguage.value,
    owned: isOwned.value,
    authored: isAuthored.value,
    available: isAvailable.value,
    courses: selectedCourses.value,
    holiday: selectedHolidays.value,
    content_types: selectedTypes.value,
    sort_order: currentSort.value
  }))

  watch(
    filtersData,
    val => {
      emit('filters-changed', val)
    },
    { immediate: true }
  )

  function reset() {
    selectedLanguage.value = ''
    searchText.value = ''
    isAuthored.value = false
    isOwned.value = false
    isAvailable.value = false
    selectedCourses.value = []
    selectedTypes.value = []
    selectedHolidays.value = []
    priceFrom.value = ''
    priceTo.value = ''
    currentSort.value = '-creation_date'
  }

  return {
    selectedLanguage,
    searchText,
    isAuthored,
    isOwned,
    isAvailable,
    selectedCourses,
    selectedTypes,
    selectedHolidays,
    priceFrom,
    priceTo,
    currentSort,
    sorts,
    filtersData,
    reset
  }
}
